import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Chip,
  LinearProgress,
  Fab,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Rating,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Phone,
  Star,
  DirectionsCar,
  Share,
  Cancel,
  MyLocation,
  LocationOn,
} from '@mui/icons-material';
import { useSnackbar } from '../contexts/SnackbarContext';
import { rideAPI } from '../services/api';
import { wsService } from '../services/websocket';
import { RIDER_EVENTS } from '../constants/websocket-events';
import { transformBackendRideToFrontend } from '../store/rideStore';
import { normalizeRideStatus } from '../constants/ride-status';
import { useCurrentLocation } from '../hooks/useCurrentLocation';
import { useRideStore } from '../store/rideStore';

interface RideData {
  id: string;
  status: string; 
  driver: {
    id: string;
    name: string;
    avatar?: string;
    rating: number;
    car: {
      make: string;
      model: string;
      color: string;
      plateNumber: string;
    };
    phone?: string;
  };
  origin: {
    address: string;
    latitude: number;
    longitude: number;
  };
  destination: {
    address: string;
    latitude: number;
    longitude: number;
  };
  driverLocation?: {
    latitude: number;
    longitude: number;
  };
  estimatedTime?: number;
  estimatedFare?: number;
  startTime?: string;
  endTime?: string;
  fare?: number;
}

declare global {
  interface Window {
    google: {
      maps: {
        Map: new (element: HTMLElement, options?: any) => any;
        LatLng: new (lat: number, lng: number) => any;
        LatLngBounds: new () => any;
        Geocoder: new () => any;
        DirectionsService: new () => any;
        DirectionsRenderer: new (options?: any) => any;
        Marker: new (options?: any) => any;
        TravelMode: {
          DRIVING: string;
        };
        ControlPosition: {
          RIGHT_CENTER: any;
        };
        SymbolPath: {
          CIRCLE: any;
        };
        DirectionsStatus: {
          OK: string;
        };
        event: {
          trigger: (instance: any, eventName: string) => void;
        };
        places: {
          AutocompleteService: new () => any;
          PlacesService: new (element: HTMLElement) => any;
        };
      };
    };
  }
}


type GoogleMap = any;
type GoogleMarker = any;
type GoogleDirectionsRenderer = any;

const RideTrackingPage: React.FC = () => {
  const { rideId } = useParams<{ rideId: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { updateCurrentRide } = useRideStore();


  const {
    location: currentLocationData,
  } = useCurrentLocation({
    watch: true,
    enableGeocoding: false, 
    enableBackendUpdates: true,
    backendUpdateInterval: 10000, 
    minDistance: 10, 
    enableHighAccuracy: true,
  });

  const [rideData, setRideData] = useState<RideData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [map, setMap] = useState<GoogleMap | null>(null);
  const [driverMarker, setDriverMarker] = useState<GoogleMarker | null>(null);
  const [originMarker, setOriginMarker] = useState<GoogleMarker | null>(null);
  const [destinationMarker, setDestinationMarker] = useState<GoogleMarker | null>(null);
  const [currentLocationMarker, setCurrentLocationMarker] = useState<GoogleMarker | null>(null); 
  const [directionsRenderer, setDirectionsRenderer] = useState<GoogleDirectionsRenderer | null>(null);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [ratingComment, setRatingComment] = useState('');
  const [isSubmittingRating, setIsSubmittingRating] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  const mapRef = useRef<HTMLDivElement>(null);

  const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyBPX_T5tQ6lbqFvCNYXi9v-orNYgcU0628';

  useEffect(() => {
    if (!rideId) return;

    const fetchRideData = async () => {
      try {
        const response = await rideAPI.getRideDetails(rideId);
        console.log('📦 API Response:', response);


        let rideDataFromAPI = null;
        if (response?.data?.data) {
          rideDataFromAPI = response.data.data;
        } else if (response?.data) {
          rideDataFromAPI = response.data;
        } else if (response?.success && response?.data) {
          rideDataFromAPI = response.data;
        } else if (response) {
          rideDataFromAPI = response;
        }

        if (!rideDataFromAPI) {
          console.error('❌ No ride data found in response');
          showSnackbar('Sifariş məlumatları tapılmadı', 'error');
          navigate('/app');
          setIsLoading(false);
          return;
        }


        const transformedRide = transformBackendRideToFrontend(rideDataFromAPI);
        if (!transformedRide) {
          console.error('❌ Failed to transform ride data');
          showSnackbar('Sifariş məlumatları işlənə bilmədi', 'error');
          navigate('/app');
          setIsLoading(false);
          return;
        }


        const mappedRideData: RideData = {
          id: transformedRide.id.toString(),
          status: normalizeRideStatus(transformedRide.status).toString() as any,
          driver: transformedRide.driver ? {
            id: transformedRide.driver.id?.toString() || transformedRide.driver_id || '',
            name: transformedRide.driver.name || transformedRide.driver.full_name || 'Sürücü',
            avatar: transformedRide.driver.avatar || transformedRide.driver.profile_picture,
            rating: transformedRide.driver.rating || transformedRide.driver.average_rating || 0,
            car: {
              make: transformedRide.driver.vehicle?.make || transformedRide.driver.car?.make || '',
              model: transformedRide.driver.vehicle?.model || transformedRide.driver.car?.model || '',
              color: transformedRide.driver.vehicle?.color || transformedRide.driver.car?.color || '',
              plateNumber: transformedRide.driver.vehicle?.plate_number || transformedRide.driver.car?.plateNumber || '',
            },
            phone: transformedRide.driver.phone || transformedRide.driver.phone_number,
          } : {
            id: '',
            name: 'Gözlənilir...',
            rating: 0,
            car: { make: '', model: '', color: '', plateNumber: '' },
          },
          origin: {
            address: transformedRide.start_point?.address || transformedRide.origin_address || '',
            latitude: transformedRide.start_point?.latitude || 0,
            longitude: transformedRide.start_point?.longitude || 0,
          },
          destination: {
            address: transformedRide.end_point?.address || transformedRide.destination_address || '',
            latitude: transformedRide.end_point?.latitude || 0,
            longitude: transformedRide.end_point?.longitude || 0,
          },
          driverLocation: transformedRide.driver?.location || transformedRide.driver?.current_location,
          estimatedFare: transformedRide.estimated_fare,
          fare: transformedRide.ride_fare,
          startTime: transformedRide.started_at,
          endTime: transformedRide.completed_at,
        };

        console.log('✅ Mapped ride data:', mappedRideData);
        setRideData(mappedRideData);
      } catch (error: any) {
        console.error('❌ Failed to fetch ride data:', error);
        showSnackbar(error?.response?.data?.message || 'Sifariş məlumatları yüklənə bilmədi', 'error');
        navigate('/app');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRideData();


    const setupWebSocket = async () => {
      try {
        await wsService.ensureConnection('rider');
        console.log('✅ WebSocket connected');


        const handleRideUpdate = (data: any) => {
          console.log('🔵 Ride update received:', data);
          let updatedRide: any = null;

          if (data.ride) {
            updatedRide = data.ride;
          } else if (data.data) {
            updatedRide = data.data;
          } else if (data.payload) {
            updatedRide = data.payload;
          } else {
            updatedRide = data;
          }

          if (updatedRide && (updatedRide.id === rideId || updatedRide.ride_id === rideId || updatedRide.rideId === rideId)) {

            const transformedRide = transformBackendRideToFrontend(updatedRide);
            if (!transformedRide) {
              console.error('❌ Failed to transform ride update');
              return;
            }

            setRideData((prev: any) => {
              if (!prev) return prev;

              const oldStatus = prev.status;
              const normalizedNewStatus = normalizeRideStatus(transformedRide.status);
              const newStatus = normalizedNewStatus.toString();


              if (oldStatus && oldStatus !== newStatus) {
                const statusMessages: Record<string, string> = {
                  'PENDING': 'Sifariş gözləyir',
                  'SEARCHING_DRIVER': 'Sürücü axtarılır',
                  'DRIVER_ASSIGNED': 'Sürücü təyin edildi',
                  'DRIVER_ARRIVED': 'Sürücü gəldi',
                  'STARTED': 'Səfər başladı',
                  'COMPLETED': 'Səfər tamamlandı',
                  'CANCELLED': 'Sifariş ləğv edildi',
                };

                const message = statusMessages[newStatus] || getStatusText(newStatus);
                showSnackbar(message, 'info');

                if (newStatus === 'COMPLETED' || newStatus === 'completed') {
                  setTimeout(() => setIsRatingModalOpen(true), 1000);
                }
              }


              return {
                ...prev,
                id: transformedRide.id.toString(),
                status: normalizedNewStatus.toString() as any,
                driver: transformedRide.driver ? {
                  id: transformedRide.driver.id?.toString() || transformedRide.driver_id || prev.driver?.id || '',
                  name: transformedRide.driver.name || transformedRide.driver.full_name || prev.driver?.name || 'Sürücü',
                  avatar: transformedRide.driver.avatar || transformedRide.driver.profile_picture || prev.driver?.avatar,
                  rating: transformedRide.driver.rating || transformedRide.driver.average_rating || prev.driver?.rating || 0,
                  car: {
                    make: transformedRide.driver.vehicle?.make || transformedRide.driver.car?.make || prev.driver?.car?.make || '',
                    model: transformedRide.driver.vehicle?.model || transformedRide.driver.car?.model || prev.driver?.car?.model || '',
                    color: transformedRide.driver.vehicle?.color || transformedRide.driver.car?.color || prev.driver?.car?.color || '',
                    plateNumber: transformedRide.driver.vehicle?.plate_number || transformedRide.driver.car?.plateNumber || prev.driver?.car?.plateNumber || '',
                  },
                  phone: transformedRide.driver.phone || transformedRide.driver.phone_number || prev.driver?.phone,
                } : prev.driver,
                origin: {
                  address: transformedRide.start_point?.address || transformedRide.origin_address || prev.origin?.address || '',
                  latitude: transformedRide.start_point?.latitude || prev.origin?.latitude || 0,
                  longitude: transformedRide.start_point?.longitude || prev.origin?.longitude || 0,
                },
                destination: {
                  address: transformedRide.end_point?.address || transformedRide.destination_address || prev.destination?.address || '',
                  latitude: transformedRide.end_point?.latitude || prev.destination?.latitude || 0,
                  longitude: transformedRide.end_point?.longitude || prev.destination?.longitude || 0,
                },
                driverLocation: transformedRide.driver?.location || transformedRide.driver?.current_location || prev.driverLocation,
                estimatedFare: transformedRide.estimated_fare || prev.estimatedFare,
                fare: transformedRide.ride_fare || prev.fare,
                startTime: transformedRide.started_at || prev.startTime,
                endTime: transformedRide.completed_at || prev.endTime,
              };
            });
          }
        };

        const handleStatusChanged = (data: any) => {
          console.log('🔵 Status changed:', data);
          if (data.ride_id === rideId || data.rideId === rideId) {
            handleRideUpdate(data);
          }
        };

        const handleDriverAssigned = (data: any) => {
          console.log('🔵 Driver assigned:', data);
          if (data.ride_id === rideId || data.rideId === rideId) {
            handleRideUpdate(data);
            showSnackbar('Sürücü təyin edildi!', 'success');
          }
        };

        const handleDriverRejected = (data: any) => {
          console.log('🔵 Driver rejected:', data);
          if (data.ride_id === rideId || data.rideId === rideId) {
            showSnackbar('Sürücü sifarişi qəbul etmədi. Başqa sürücü axtarılır...', 'warning');

            if (rideData) {
              setRideData((prev: any) => ({
                ...prev,
                status: 'SEARCHING_DRIVER',
                driver: undefined,
              }));
            }
          }
        };

        const handleDriverArrived = (data: any) => {
          console.log('🔵 Driver arrived:', data);
          if (data.ride_id === rideId || data.rideId === rideId) {
            handleRideUpdate(data);
            showSnackbar('Sürücü gəldi!', 'success');
          }
        };

        const handleRideStarted = (data: any) => {
          console.log('🔵 Ride started:', data);
          if (data.ride_id === rideId || data.rideId === rideId) {
            handleRideUpdate(data);
            showSnackbar('Səfər başladı!', 'success');
          }
        };

        const handleDriverLocation = (data: any) => {
          console.log('🔵 Driver location update:', data);
          if (data.ride_id === rideId || data.rideId === rideId || !data.ride_id) {
            if (data.location) {
              updateDriverLocation(data.location);
            } else if (data.latitude && data.longitude) {
              updateDriverLocation({ latitude: data.latitude, longitude: data.longitude });
            } else if (data.payload) {
              updateDriverLocation(data.payload);
            }
          }
        };

        const handleRideCompleted = (data: any) => {
          console.log('🔵 Ride completed:', data);
          if (data.ride_id === rideId || data.rideId === rideId) {
            handleRideUpdate(data);
            showSnackbar('Səfər tamamlandı!', 'success');

            window.dispatchEvent(new CustomEvent('rideCompleted', { detail: { rideId: rideId } }));
            setTimeout(() => setIsRatingModalOpen(true), 1000);
          }
        };

        const handleRideCancelled = (data: any) => {
          console.log('🔵 Ride cancelled:', data);
          const dataRideId = data.ride_id || data.rideId || data.id || data.ride?.id;
          if (dataRideId === rideId || dataRideId?.toString() === rideId?.toString()) {
            handleRideUpdate(data);
            showSnackbar('Sifariş ləğv edildi', 'warning');

            updateCurrentRide(null);
            setRideData(null);
            setTimeout(() => {
              navigate('/app');
            }, 1500); 
          }
        };


        wsService.on(RIDER_EVENTS.RIDE_UPDATED, handleRideUpdate);
        wsService.on(RIDER_EVENTS.RIDE_STATUS_CHANGED, handleStatusChanged);
        wsService.on(RIDER_EVENTS.RIDE_DRIVER_ASSIGNED, handleDriverAssigned);
        wsService.on(RIDER_EVENTS.RIDE_DRIVER_REJECTED, handleDriverRejected);
        wsService.on(RIDER_EVENTS.RIDE_DRIVER_ARRIVED, handleDriverArrived);
        wsService.on(RIDER_EVENTS.RIDE_STARTED, handleRideStarted);
        wsService.on(RIDER_EVENTS.RIDE_COMPLETED, handleRideCompleted);
        wsService.on(RIDER_EVENTS.RIDE_CANCELLED, handleRideCancelled);
        wsService.on(RIDER_EVENTS.DRIVER_LOCATION_UPDATED, handleDriverLocation);
        wsService.on('driver_location', handleDriverLocation);
        wsService.on('ride:driver_rejected', handleDriverRejected);
        wsService.on('driver:rejected', handleDriverRejected);
        wsService.on('ride_completed', handleRideCompleted);


        return () => {
          wsService.off(RIDER_EVENTS.RIDE_UPDATED, handleRideUpdate);
          wsService.off(RIDER_EVENTS.RIDE_STATUS_CHANGED, handleStatusChanged);
          wsService.off(RIDER_EVENTS.RIDE_DRIVER_ASSIGNED, handleDriverAssigned);
          wsService.off(RIDER_EVENTS.RIDE_DRIVER_ARRIVED, handleDriverArrived);
          wsService.off(RIDER_EVENTS.RIDE_STARTED, handleRideStarted);
          wsService.off(RIDER_EVENTS.RIDE_COMPLETED, handleRideCompleted);
          wsService.off(RIDER_EVENTS.RIDE_CANCELLED, handleRideCancelled);
          wsService.off(RIDER_EVENTS.DRIVER_LOCATION_UPDATED, handleDriverLocation);
          wsService.off('driver_location', handleDriverLocation);
          wsService.off('ride_completed', handleRideCompleted);
        };
      } catch (error) {
        console.error('❌ Failed to establish WebSocket connection:', error);
      }
    };

    setupWebSocket();
  }, [rideId, navigate, showSnackbar]);


  useEffect(() => {

    if (window.google && window.google.maps) {
      console.log('✅ Google Maps already loaded');
      setIsGoogleMapsLoaded(true);
      return;
    }


    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      console.log('⏳ Google Maps script already exists, waiting for load...');
      const checkInterval = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkInterval);
          console.log('✅ Google Maps loaded from existing script');
          setIsGoogleMapsLoaded(true);
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        if (!window.google || !window.google.maps) {
          console.error('❌ Google Maps failed to load from existing script');
        }
      }, 10000);
      return;
    }


    console.log('📦 Loading Google Maps script...');
    const script = document.createElement('script');
    script.id = 'google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry&loading=async`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      console.log('✅ Google Maps script loaded');
      setTimeout(() => {
        if (window.google && window.google.maps) {
          setIsGoogleMapsLoaded(true);
        } else {
          console.error('❌ Google Maps not available after script load');
          showSnackbar('Google Maps yüklənə bilmədi', 'error');
        }
      }, 300);
    };

    script.onerror = () => {
      console.error('❌ Failed to load Google Maps script');
      showSnackbar('Google Maps yüklənə bilmədi', 'error');
    };

    document.head.appendChild(script);
  }, [GOOGLE_MAPS_API_KEY, showSnackbar]);


  useEffect(() => {
    if (!mapRef.current || !isGoogleMapsLoaded || !window.google || !window.google.maps) {
      console.log('⏳ Waiting for map ref or Google Maps...', {
        hasMapRef: !!mapRef.current,
        isGoogleMapsLoaded,
        hasGoogle: !!window.google,
        hasMaps: !!(window.google && window.google.maps),
      });
      return;
    }

    if (map) {
      console.log('⚠️ Map already initialized');
      return;
    }

    console.log('🗺️ Initializing map...');

    let mapInstance: GoogleMap | null = null;

    try {

      if (!mapRef.current) {
        console.error('❌ Map ref is null');
        return;
      }


      const centerLat = rideData?.origin?.latitude || 40.4093;
      const centerLng = rideData?.origin?.longitude || 49.8671;

      mapInstance = new window.google.maps.Map(mapRef.current, {
        center: { lat: centerLat, lng: centerLng },
        zoom: rideData ? 15 : 13,
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        gestureHandling: 'cooperative',
        disableDefaultUI: false,
        zoomControl: true,
        zoomControlOptions: {
          position: (window.google.maps as any).ControlPosition?.RIGHT_CENTER || undefined,
        },
      });


      setTimeout(() => {
        if (mapInstance) {
          try {
            const center = mapInstance.getCenter();
            if (center) {
              mapInstance.setCenter(center);
            }
          } catch (e) {
            console.warn('⚠️ Could not set center:', e);
          }
        }
      }, 100);

      setMap(mapInstance);
      console.log('✅ Map initialized');

      const directionsRendererInstance = new window.google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#FF006E',
          strokeWeight: 5,
          strokeOpacity: 0.8,
        },
      });

      directionsRendererInstance.setMap(mapInstance);
      setDirectionsRenderer(directionsRendererInstance);
      console.log('✅ Directions renderer initialized');


      setTimeout(() => {
        if (mapInstance && window.google && window.google.maps) {
          try {
            if ((window.google.maps as any).event && (window.google.maps as any).event.trigger) {
              (window.google.maps as any).event.trigger(mapInstance, 'resize');
            }
            const center = mapInstance.getCenter();
            if (center) {
              mapInstance.setCenter(center);
            }
          } catch (e) {
            console.warn('⚠️ Initial resize failed:', e);
          }
        }
      }, 100);


      setTimeout(() => {
        if (mapInstance && window.google && window.google.maps) {
          try {

            if ((window.google.maps as any).event && (window.google.maps as any).event.trigger) {
              (window.google.maps as any).event.trigger(mapInstance, 'resize');
              console.log('✅ Map resize triggered via event');
            } else {

              const currentZoom = mapInstance.getZoom();
              mapInstance.setZoom(currentZoom || 13);
              console.log('✅ Map resize triggered via zoom');
            }
          } catch (e) {
            console.warn('⚠️ Could not trigger resize:', e);

            try {
              const center = mapInstance.getCenter();
              if (center) {
                mapInstance.setCenter(center);
              }
            } catch (e2) {
              console.warn('⚠️ Could not force resize:', e2);
            }
          }
        }
      }, 1000);


    } catch (error) {
      console.error('❌ Error initializing map:', error);
      showSnackbar('Xəritə başlatıla bilmədi', 'error');
    }

    return () => {

      if (currentLocationMarker) {
        currentLocationMarker.setMap(null);
      }
    };
  }, [isGoogleMapsLoaded, map, showSnackbar, rideData, currentLocationMarker]);


  useEffect(() => {
    if (currentLocationData && map && window.google && window.google.maps) {
      const { latitude, longitude } = currentLocationData;

      if (currentLocationMarker) {

        currentLocationMarker.setPosition({ lat: latitude, lng: longitude });
      } else {

        const marker = new window.google.maps.Marker({
          position: { lat: latitude, lng: longitude },
          map: map,
          title: 'Mənim mövqeyim',
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 8,
            fillColor: '#2196F3',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
          },
        });
        setCurrentLocationMarker(marker);
      }
    }
  }, [currentLocationData, map, currentLocationMarker]);

  useEffect(() => {
    if (!rideData || !map || !directionsRenderer) return;


    const bounds = new window.google.maps.LatLngBounds();


    if (currentLocationData) {
      bounds.extend({ lat: currentLocationData.latitude, lng: currentLocationData.longitude });
    }


    const originPosition = { lat: rideData.origin.latitude, lng: rideData.origin.longitude };
    bounds.extend(originPosition);


    if (originMarker) {
      originMarker.setPosition(originPosition);
    } else {
      const marker = new window.google.maps.Marker({
        position: originPosition,
        map: map,
        title: 'Götürüləcək yer',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4CAF50',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
      });
      setOriginMarker(marker);
    }


    const destinationPosition = { lat: rideData.destination.latitude, lng: rideData.destination.longitude };
    bounds.extend(destinationPosition);


    if (destinationMarker) {
      destinationMarker.setPosition(destinationPosition);
      destinationMarker.setMap(map); 
    } else {
      const marker = new window.google.maps.Marker({
        position: destinationPosition,
        map: map,
        title: 'Təyinat məntəqəsi',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#F44336',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
      });
      setDestinationMarker(marker);
    }


    if (rideData.driverLocation) {
      const driverPosition = {
        lat: rideData.driverLocation.latitude,
        lng: rideData.driverLocation.longitude,
      };
      bounds.extend(driverPosition);
      updateDriverLocation(rideData.driverLocation);
    }


    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        top: 100,
        right: 50,
        bottom: 300,
        left: 50,
      });
    }


    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: originPosition,
        destination: destinationPosition,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result: any, status: string) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          directionsRenderer.setDirections(result);
        } else {
          console.warn('Route calculation failed:', status);
        }
      }
    );
  }, [rideData, map, directionsRenderer, driverMarker, originMarker, destinationMarker, currentLocationData]);

  const updateDriverLocation = (location: { latitude: number; longitude: number }) => {
    if (!map) return;

    const position = { lat: location.latitude, lng: location.longitude };

    if (!driverMarker) {
      const marker = new window.google.maps.Marker({
        position,
        map,
        title: 'Sürücü',
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#FF006E',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
      });
      setDriverMarker(marker);
    } else {
      driverMarker.setPosition(position);
    }
  };

  const handleCallDriver = () => {
    if (rideData?.driver.phone) {
      window.location.href = `tel:${rideData.driver.phone}`;
    }
  };

  const handleShareRide = () => {
    if (!rideData) return;

    const shareUrl = `${window.location.origin}/app/track/${rideData.id}`;

    if (navigator.share) {
      navigator.share({
        title: 'Ozio - Sifarişimi izlə',
        text: `Mən ${rideData.origin.address} ünvanından ${rideData.destination.address} ünvanına gedirəm.`,
        url: shareUrl,
      }).catch(err => console.error('Error sharing:', err));
    } else {

      navigator.clipboard.writeText(shareUrl)
        .then(() => showSnackbar('Keçid köçürülüdü', 'success'))
        .catch(() => showSnackbar('Keçid köçürülə bilmədi', 'error'));
    }
  };

  const handleCancelRideClick = () => {
    if (!rideData) return;


    const normalizedStatus = rideData.status.toUpperCase();
    const cannotCancel = [
      'COMPLETED',
      'CANCELLED',
      'STARTED',
      'IN_PROGRESS',
      'DRIVER_ARRIVED',
    ].includes(normalizedStatus);

    if (cannotCancel) {
      showSnackbar('Bu sifarişi ləğv etmək mümkün deyil', 'warning');
      return;
    }

    setIsCancelDialogOpen(true);
  };

  const handleCancelRide = async () => {
    if (!rideData) return;

    setIsCancelDialogOpen(false);

    try {
      await rideAPI.cancelRide(rideData.id, 'İstifadəçi tərəfindən ləğv edildi');
      showSnackbar('Sifariş ləğv edildi', 'success');
      navigate('/app');
    } catch (error: any) {
      console.error('Cancel ride error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Sifariş ləğv edilə bilmədi';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleCenterOnDriver = () => {
    if (!rideData?.driverLocation || !map) return;

    map.setCenter({
      lat: rideData.driverLocation.latitude,
      lng: rideData.driverLocation.longitude,
    });
  };

  const handleCenterOnOrigin = () => {
    if (!rideData?.origin || !map) return;

    map.setCenter({
      lat: rideData.origin.latitude,
      lng: rideData.origin.longitude,
    });
  };

  const handleSubmitRating = () => {
    if (!rideData || !rideData.driver?.id) return;

    setIsSubmittingRating(true);



    const rideIdNum = parseInt(rideData.id, 10);
    const ratedUserIdNum = parseInt(rideData.driver.id, 10);


    if (!rideIdNum || rideIdNum <= 0) {
      showSnackbar('Sifariş ID düzgün deyil', 'error');
      setIsSubmittingRating(false);
      return;
    }

    if (!ratedUserIdNum || ratedUserIdNum <= 0) {
      showSnackbar('Sürücü ID düzgün deyil', 'error');
      setIsSubmittingRating(false);
      return;
    }

      rideAPI.rateRide(rideIdNum.toString(), ratedUserIdNum.toString(), 'driver', rating)
        .then(() => {
          showSnackbar('Rəyiniz üçün təşəkkür edirik!', 'success');
          setIsRatingModalOpen(false);

          window.dispatchEvent(new CustomEvent('rideCompleted', { detail: { rideId: rideIdNum } }));

          navigate('/app/history');
        })
      .catch((error: any) => {
        console.error('Rating error:', error);
        const errorMessage = error?.response?.data?.message || error?.message || 'Rəyiniz göndərilə bilmədi';
        showSnackbar(errorMessage, 'error');
      })
      .finally(() => {
        setIsSubmittingRating(false);
      });
  };

  const getStatusText = (status: string) => {
    const normalizedStatus = status.toUpperCase();
    switch (normalizedStatus) {
      case 'PENDING':
        return 'Gözləyir';
      case 'SEARCHING_DRIVER':
        return 'Sürücü axtarılır';
      case 'DRIVER_ASSIGNED':
      case 'ACCEPTED':
        return 'Sürücü təyin edildi';
      case 'DRIVER_ARRIVED':
      case 'ARRIVING':
        return 'Sürücü gəldi';
      case 'STARTED':
      case 'IN_PROGRESS':
        return 'Səyahət davam edir';
      case 'COMPLETED':
        return 'Səyahət bitdi';
      case 'CANCELLED':
        return 'Sifariş ləğv edildi';
      case 'NO_DRIVERS_AVAILABLE':
        return 'Sürücü tapılmadı';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    const normalizedStatus = status.toUpperCase();
    switch (normalizedStatus) {
      case 'PENDING':
      case 'SEARCHING_DRIVER':
        return 'warning';
      case 'DRIVER_ASSIGNED':
      case 'ACCEPTED':
        return 'info';
      case 'DRIVER_ARRIVED':
      case 'ARRIVING':
        return 'primary';
      case 'STARTED':
      case 'IN_PROGRESS':
        return 'success';
      case 'COMPLETED':
        return 'success';
      case 'CANCELLED':
      case 'NO_DRIVERS_AVAILABLE':
        return 'error';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!rideData) {
    return (
      <Box p={2}>
        <Alert severity="error">Sifariş məlumatları tapılmadı</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          zIndex: 1000,
          position: 'relative',
          borderRadius: 0,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Sifariş №{rideData.id.slice(-8)}</Typography>
          <Chip
            label={getStatusText(rideData.status)}
            color={getStatusColor(rideData.status) as any}
            size="small"
          />
        </Box>

        {}
        {rideData.status !== 'CANCELLED' && rideData.status !== 'cancelled' && (
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Avatar
                src={rideData.driver.avatar}
                alt={rideData.driver.name}
                sx={{ width: 56, height: 56, mr: 2 }}
              />
              <Box flexGrow={1}>
                <Typography variant="subtitle1">{rideData.driver.name}</Typography>
                <Box display="flex" alignItems="center">
                  <Star color="warning" fontSize="small" />
                  <Typography variant="body2" ml={0.5}>
                    {rideData.driver.rating}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {rideData.driver.car.make} {rideData.driver.car.model} -{' '}
                  {rideData.driver.car.color}, {rideData.driver.car.plateNumber}
                </Typography>
              </Box>
              {rideData.driver.phone && (
                <IconButton 
                  color="primary" 
                  onClick={handleCallDriver}
                  sx={{
                    minWidth: { xs: 48, sm: 56 },
                    minHeight: { xs: 48, sm: 56 },
                    padding: { xs: 1.5, sm: 2 },
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent',
                    '&:active': {
                      transform: 'scale(0.95)',
                      transition: 'transform 0.1s ease-out',
                    },
                  }}
                >
                  <Phone />
                </IconButton>
              )}
            </CardContent>
          </Card>
        )}

        {}
        <Box display="flex" alignItems="center" mb={2}>
          <LocationOn color="primary" sx={{ mr: 1 }} />
          <Typography variant="body2" noWrap>
            {rideData.origin.address}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <LocationOn color="secondary" sx={{ mr: 1 }} />
          <Typography variant="body2" noWrap>
            {rideData.destination.address}
          </Typography>
        </Box>
      </Paper>

      {}
      <Box sx={{ flex: 1, position: 'relative', minHeight: 0, overflow: 'hidden', width: '100%' }}>
        <div
          ref={mapRef}
          style={{ 
            width: '100%', 
            height: '100%',
            minHeight: '400px',
            backgroundColor: '#e0e0e0',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: map ? 1 : 0,
          }}
        />
        {!isGoogleMapsLoaded && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              zIndex: 1000,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Xəritə yüklənir...</Typography>
            </Box>
          </Box>
        )}
        {isGoogleMapsLoaded && !map && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 999,
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography>Xəritə başlatılır...</Typography>
            </Box>
          </Box>
        )}

        {}
        <Box
          sx={{
            position: 'absolute',
            bottom: 20,
            right: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {rideData.driverLocation && (
            <Fab 
              size="small" 
              color="primary" 
              onClick={handleCenterOnDriver}
              sx={{
                minWidth: { xs: 48, sm: 56 },
                minHeight: { xs: 48, sm: 56 },
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                '&:active': {
                  transform: 'scale(0.95)',
                  transition: 'transform 0.1s ease-out',
                },
              }}
            >
              <DirectionsCar />
            </Fab>
          )}
          <Fab 
            size="small" 
            color="primary" 
            onClick={handleCenterOnOrigin}
            sx={{
              minWidth: { xs: 48, sm: 56 },
              minHeight: { xs: 48, sm: 56 },
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.95)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            <MyLocation />
          </Fab>
        </Box>
      </Box>

      {}
      <Paper
        elevation={3}
        sx={{
          p: 2,
          zIndex: 1000,
          position: 'relative',
          borderRadius: 0,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={1}>
          {rideData.estimatedTime && (
            <Typography variant="body2">
              Təxmin zamanda: {rideData.estimatedTime} dəqiqə
            </Typography>
          )}
          {rideData.fare && (
            <Typography variant="h6" color="primary">
              ₼{rideData.fare.toFixed(2)}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box display="flex" justifyContent="space-between" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Share />}
            onClick={handleShareRide}
            sx={{ 
              flex: 1,
              minHeight: { xs: 56, sm: 48 },
              py: { xs: 2, sm: 1.5 },
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            Paylaş
          </Button>

          {(() => {
            const normalizedStatus = rideData.status.toUpperCase();


            const canCancel = ![
              'DRIVER_ARRIVED',
              'ARRIVING',
              'STARTED',
              'IN_PROGRESS',
              'COMPLETED',
              'CANCELLED',
            ].includes(normalizedStatus);

            return canCancel ? (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={handleCancelRideClick}
                sx={{ 
                  flex: 1,
                  minHeight: { xs: 56, sm: 48 },
                  py: { xs: 2, sm: 1.5 },
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  '&:active': {
                    transform: 'scale(0.98)',
                    transition: 'transform 0.1s ease-out',
                  },
                }}
              >
                Ləğv et
              </Button>
            ) : null;
          })()}
        </Box>
      </Paper>

      {}
      <Dialog
        open={isRatingModalOpen}
        onClose={() => setIsRatingModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Səyahəti qiymətləndirin</DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2, textAlign: 'center' }}>
            <Rating
              name="rating"
              value={rating}
              onChange={(_event, newValue) => {
                setRating(newValue || 5);
              }}
              size="large"
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Rəyiniz (istəyə bağlı)"
            value={ratingComment}
            onChange={(e) => setRatingComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
          <Button 
            onClick={() => setIsRatingModalOpen(false)}
            sx={{
              minHeight: { xs: 56, sm: 48 },
              py: { xs: 2, sm: 1.5 },
              px: { xs: 3, sm: 2 },
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            Bağla
          </Button>
          <Button
            onClick={handleSubmitRating}
            disabled={isSubmittingRating}
            variant="contained"
            sx={{
              minHeight: { xs: 56, sm: 48 },
              py: { xs: 2, sm: 1.5 },
              px: { xs: 3, sm: 2 },
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              '&:active': {
                transform: 'scale(0.98)',
                transition: 'transform 0.1s ease-out',
              },
            }}
          >
            Göndər
          </Button>
        </DialogActions>
      </Dialog>

      {}
      <Dialog
        open={isCancelDialogOpen}
        onClose={() => setIsCancelDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: { xs: 3, sm: 4 },
            minWidth: { xs: '280px', sm: '400px' },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Sifarişi ləğv et
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {rideData && (rideData.status.toUpperCase() === 'DRIVER_ASSIGNED' || rideData.status.toUpperCase() === 'ACCEPTED')
              ? 'Sürücü təyin edilib. Sifarişi ləğv etmək istədiyinizə əminsiniz?'
              : 'Sifarişi ləğv etmək istədiyinizə əminsiniz?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 }, pt: 1 }}>
          <Button
            onClick={() => setIsCancelDialogOpen(false)}
            variant="outlined"
            sx={{
              minWidth: { xs: 100, sm: 120 },
              minHeight: { xs: 44, sm: 48 },
              touchAction: 'manipulation',
            }}
          >
            Xeyr
          </Button>
          <Button
            onClick={handleCancelRide}
            variant="contained"
            color="error"
            sx={{
              minWidth: { xs: 100, sm: 120 },
              minHeight: { xs: 44, sm: 48 },
              touchAction: 'manipulation',
            }}
          >
            Bəli
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RideTrackingPage;
